import { InlineAssignment } from '../../services/assignmentPreview';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plane,
  Building,
  Car,
  Utensils,
  Users,
  Calendar,
  MapPin,
  Clock,
  Trash2,
  ArrowRight,
  Loader2,
  Inbox,
  ChevronDown,
  Hash,
  User,
  Briefcase,
  Lock
} from 'lucide-react';
import { Button } from '../ui/button';
import { listUnfilledComponents, assignUnfilledComponent, deleteUnfilledComponent, fileUnfilledComponent } from '../../services/emailAddressService';
import { useUnfiledComponentEvents } from '../../contexts/SSEContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import ConfidentialPlaceholder from './ConfidentialPlaceholder';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil, formatTimePreserveTimezone } from '../../utils/dateTimeUtils';

// Component type styles matching Timeline 2.0
const componentTypeStyles = {
  hotel: {
    backgroundColor: 'rgb(var(--ui-indigo-bg))',
    borderColor: 'rgb(var(--ui-indigo-line))',
    iconColor: 'rgb(var(--ui-indigo-fg))',
    badgeColor: 'bg-ui-indigo-bg text-ui-indigo-fg border-ui-indigo-line'
  },
  meeting: {
    backgroundColor: 'rgb(var(--ui-green-bg))',
    borderColor: 'rgb(var(--ui-green-line))',
    iconColor: 'rgb(var(--ui-green-fg))',
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  restaurant: {
    backgroundColor: 'rgb(var(--ui-orange-bg))',
    borderColor: 'rgb(var(--ui-orange-line))',
    iconColor: 'rgb(var(--ui-orange-fg))',
    badgeColor: 'bg-ui-orange-bg text-ui-orange-fg border-ui-orange-line'
  },
  private_flight: {
    backgroundColor: 'rgb(var(--ui-violet-bg))',
    borderColor: 'rgb(var(--ui-violet-line))',
    iconColor: 'rgb(var(--ui-violet-fg))',
    badgeColor: 'bg-ui-violet-bg text-ui-violet-fg border-ui-violet-line'
  },
  commercial_flight: {
    backgroundColor: 'rgb(var(--ui-blue-bg))',
    borderColor: 'rgb(var(--ui-blue-line))',
    iconColor: 'rgb(var(--ui-blue-fg))',
    badgeColor: 'bg-ui-blue-bg text-ui-blue-fg border-ui-blue-line'
  },
  rental_car: {
    backgroundColor: 'rgb(var(--ui-green-bg))',
    borderColor: 'rgb(var(--ui-green-line))',
    iconColor: 'rgb(var(--ui-green-fg))',
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  car_rental: {
    backgroundColor: 'rgb(var(--ui-green-bg))',
    borderColor: 'rgb(var(--ui-green-line))',
    iconColor: 'rgb(var(--ui-green-fg))',
    badgeColor: 'bg-ui-green-bg text-ui-green-fg border-ui-green-line'
  },
  event: {
    backgroundColor: 'rgb(var(--ui-pink-bg))',
    borderColor: 'rgb(var(--ui-pink-line))',
    iconColor: 'rgb(var(--ui-pink-fg))',
    badgeColor: 'bg-ui-pink-bg text-ui-pink-fg border-ui-pink-line'
  },
  general: {
    backgroundColor: 'rgb(var(--ui-sunk))',
    borderColor: 'rgb(var(--ui-line))',
    iconColor: 'rgb(var(--ui-muted))',
    badgeColor: 'bg-ui-hover text-ui-ink-2 border-ui-control'
  }
};

// Get icon for component type
const getTypeIcon = (type) => {
  const iconClass = "w-5 h-5";
  switch (type) {
    case 'commercial_flight':
    case 'private_flight':
    case 'flight':
      return <Plane className={iconClass} />;
    case 'hotel':
      return <Building className={iconClass} />;
    case 'rental_car':
    case 'car_rental':
      return <Car className={iconClass} />;
    case 'restaurant':
      return <Utensils className={iconClass} />;
    case 'meeting':
      return <Users className={iconClass} />;
    case 'event':
      return <Calendar className={iconClass} />;
    default:
      return <Calendar className={iconClass} />;
  }
};

// Get display name for component type
const getTypeName = (type) => {
  const names = {
    'commercial_flight': 'Flight',
    'private_flight': 'Private Flight',
    'hotel': 'Hotel',
    'rental_car': 'Car Rental',
    'car_rental': 'Car Rental',
    'restaurant': 'Restaurant',
    'meeting': 'Meeting',
    'event': 'Event'
  };
  return names[type] || type?.replace(/_/g, ' ') || 'Component';
};


/**
 * Individual unfiled item card in Timeline 2.0 style
 */
const UnfiledItemCard = ({
  item,
  trips,
  selectedTripId,
  onTripSelect,
  onAssign,
  onDelete,
  isAssigning,
  isDeleting
}) => {
  const { user } = useAuth();
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };
  const typeStyle = componentTypeStyles[item.component_type] || componentTypeStyles.general;
  const cd = item.component_data || {};

  // Get location/itinerary based on component type
  const getLocation = () => {
    if (item.component_type === 'hotel') {
      return cd.hotel_name || cd.address || item.location;
    }
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        // Build full itinerary with all legs: TLV → GYD → TLV
        const airports = [];
        legs.forEach((leg, index) => {
          const dep = leg.departure_airport || leg.departure_airport_code || '';
          if (dep && (index === 0 || airports[airports.length - 1] !== dep)) {
            airports.push(dep);
          }
          const arr = leg.arrival_airport || leg.arrival_airport_code || '';
          if (arr) {
            airports.push(arr);
          }
        });
        return airports.length > 0 ? airports.join(' → ') : item.location;
      }
      return cd.departure_airport && cd.arrival_airport
        ? `${cd.departure_airport} → ${cd.arrival_airport}`
        : item.location;
    }
    if (item.component_type === 'rental_car' || item.component_type === 'car_rental') {
      return cd.rental_company || cd.pickup_location || item.location;
    }
    return item.location;
  };

  // Get date/time range for flights (first leg departure to last leg arrival)
  // Uses user preferences for date format (EU/US) and time format (24H/AMPM)
  const getDateTimeRange = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];

        // Get departure datetime from first leg - prefer full datetime, then construct from date+time
        const depDateStr = firstLeg.departure_datetime || (firstLeg.departure_date && firstLeg.departure_time ? `${firstLeg.departure_date}T${firstLeg.departure_time}` : null) || firstLeg.departure_date || firstLeg.departure_time;
        // Get arrival datetime from last leg
        const arrDateStr = lastLeg.arrival_datetime || (lastLeg.arrival_date && lastLeg.arrival_time ? `${lastLeg.arrival_date}T${lastLeg.arrival_time}` : null) || lastLeg.arrival_date || lastLeg.arrival_time;

        if (depDateStr && arrDateStr) {
          // Use preference-aware formatting (no day of week to fit on one line)
          const depDate = formatDateUtil(depDateStr, prefs);
          const depTime = formatTimePreserveTimezone(depDateStr, prefs) || formatTimeUtil(depDateStr, prefs);
          const arrDate = formatDateUtil(arrDateStr, prefs);
          const arrTime = formatTimePreserveTimezone(arrDateStr, prefs) || formatTimeUtil(arrDateStr, prefs);

          // Format: "01/15/2025 11:15 AM - 01/16/2025 3:30 PM" (US) or "15/01/2025 11:15 - 16/01/2025 15:30" (EU/24H)
          const startStr = depTime ? `${depDate} ${depTime}` : depDate;
          const endStr = arrTime ? `${arrDate} ${arrTime}` : arrDate;

          return { start: startStr, end: endStr, isRange: true };
        } else if (depDateStr) {
          const depDate = formatDateUtil(depDateStr, prefs);
          const depTime = formatTimePreserveTimezone(depDateStr, prefs) || formatTimeUtil(depDateStr, prefs);
          return { start: depTime ? `${depDate} ${depTime}` : depDate, end: null, isRange: false };
        }
      }
    }
    // Fall back to item's start_datetime
    if (item.start_datetime) {
      const date = formatDateUtil(item.start_datetime, prefs);
      const time = formatTimePreserveTimezone(item.start_datetime, prefs) || formatTimeUtil(item.start_datetime, prefs);
      return { start: time ? `${date} ${time}` : date, end: null, isRange: false };
    }
    return null;
  };

  // Get passenger/traveler name for flights
  const getPassengerName = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      // Check for traveler_name first
      if (cd.traveler_name) {
        return cd.traveler_name;
      }
      // Check passengers array
      const passengers = cd.passengers || [];
      if (passengers.length > 0) {
        // Return the first passenger's name (for exploded confirmations, there's only one)
        const passengerName = passengers[0]?.name || passengers[0];
        if (typeof passengerName === 'string') {
          return passengerName;
        }
      }
    }
    return null;
  };

  // Get additional details based on component type
  const getDetails = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        return legs.map((leg, i) => {
          const airline = leg.airline_name || leg.airline || '';
          const flight = leg.flight_number || '';
          return airline && flight ? `${airline} ${flight}` : (flight || airline);
        }).filter(Boolean).join(' • ');
      }
      const airline = cd.airline_name || cd.airline || '';
      const flight = cd.flight_number || '';
      return airline && flight ? `${airline} ${flight}` : (flight || airline);
    }
    if (item.component_type === 'hotel') {
      const checkIn = cd.check_in_date ? formatDateUtil(cd.check_in_date, prefs) : null;
      const checkOut = cd.check_out_date ? formatDateUtil(cd.check_out_date, prefs) : null;
      if (checkIn && checkOut) {
        return `Check-in: ${checkIn} • Check-out: ${checkOut}`;
      }
    }
    if (item.component_type === 'rental_car' || item.component_type === 'car_rental') {
      return cd.car_type || cd.vehicle_type || '';
    }
    return '';
  };

  return (
    <div
      className="rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-lg"
      style={{
        backgroundColor: `${typeStyle.backgroundColor}`,
        borderColor: typeStyle.borderColor
      }}
    >
      {/* Header: Icon + Title + Type Badge */}
      <div className="flex items-start gap-3 mb-3">
        {/* Icon */}
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: typeStyle.borderColor,
            color: typeStyle.iconColor
          }}
        >
          {getTypeIcon(item.component_type)}
        </div>

        <div className="flex-1 min-w-0">
          {/* Title Row */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-semibold text-ui-ink leading-tight">
              {item.title || getTypeName(item.component_type)}
            </h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeStyle.badgeColor}`}>
              {getTypeName(item.component_type)}
            </span>
            {item.is_confidential_inbox && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-red-bg text-ui-red-fg border border-ui-red-line">
                <Lock className="h-3 w-3" />
                Confidential
              </span>
            )}
          </div>

          {/* Date/Time - use range for flights */}
          {(() => {
            const dateTimeRange = getDateTimeRange();
            if (dateTimeRange) {
              return (
                <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    {dateTimeRange.isRange
                      ? `${dateTimeRange.start} - ${dateTimeRange.end}`
                      : dateTimeRange.start}
                  </span>
                </div>
              );
            }
            return null;
          })()}

          {/* Itinerary/Location */}
          {getLocation() && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{getLocation()}</span>
            </div>
          )}

          {/* Passenger Name - for flights */}
          {getPassengerName() && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
              <User className="w-3.5 h-3.5" />
              <span>{getPassengerName()}</span>
            </div>
          )}

          {/* Additional Details */}
          {getDetails() && (
            <div className="text-sm text-ui-muted">
              {getDetails()}
            </div>
          )}

          {/* Confirmation Number */}
          {item.confirmation_number && (
            <div className="flex items-center gap-1 text-sm text-ui-muted mt-1">
              <Hash className="w-3.5 h-3.5" />
              <span>{item.confirmation_number}</span>
            </div>
          )}

          {/* Executive Name */}
          {item.executive_name && (
            <div className="flex items-center gap-1 text-sm text-ui-muted mt-1">
              <User className="w-3.5 h-3.5" />
              <span>{item.executive_name}</span>
            </div>
          )}
        </div>

        {/* Delete Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(item.id)}
          disabled={isDeleting}
          className="h-8 w-8 text-ui-faint hover:text-ui-red-fg hover:bg-ui-red-bg flex-shrink-0"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Assign to Trip Section */}
      <div className="space-y-2 pt-3 border-t border-ui-divider">
        <div className="relative flex-1">
          <select
            value={selectedTripId || ''}
            onChange={(e) => onTripSelect(item.id, e.target.value)}
            className="w-full h-9 pl-3 pr-8 text-sm border border-ui-line rounded-lg bg-ui-surface text-ui-ink-2 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid appearance-none"
          >
            <option value="">Assign to trip...</option>
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.title || 'Untitled Trip'}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ui-faint pointer-events-none" />
        </div>
        <InlineAssignment componentId={item.id} tripId={selectedTripId} busy={isAssigning}
          fallbackLabel="Assign" onApply={preview => onAssign(item.id, preview)} />
      </div>
    </div>
  );
};

/**
 * EA Unfiled Item Card - For items that haven't been assigned to an executive yet
 * Has TWO dropdowns: one for executive, one for trip
 */
const EAUnfiledItemCard = ({
  item,
  trips,
  executives,
  selectedExecutiveId,
  selectedTripId,
  onExecutiveSelect,
  onTripSelect,
  onFile,
  onDelete,
  isFiling,
  isDeleting
}) => {
  const { user } = useAuth();
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };
  const typeStyle = componentTypeStyles[item.component_type] || componentTypeStyles.general;
  const cd = item.component_data || {};

  // Get location/itinerary based on component type
  const getLocation = () => {
    if (item.component_type === 'hotel') {
      return cd.hotel_name || cd.address || item.location;
    }
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        const airports = [];
        legs.forEach((leg, index) => {
          const dep = leg.departure_airport || leg.departure_airport_code || '';
          if (dep && (index === 0 || airports[airports.length - 1] !== dep)) {
            airports.push(dep);
          }
          const arr = leg.arrival_airport || leg.arrival_airport_code || '';
          if (arr) {
            airports.push(arr);
          }
        });
        return airports.length > 0 ? airports.join(' → ') : item.location;
      }
      return cd.departure_airport && cd.arrival_airport
        ? `${cd.departure_airport} → ${cd.arrival_airport}`
        : item.location;
    }
    if (item.component_type === 'rental_car' || item.component_type === 'car_rental') {
      return cd.rental_company || cd.pickup_location || item.location;
    }
    return item.location;
  };

  // Get date/time range for flights
  // Uses user preferences for date format (EU/US) and time format (24H/AMPM)
  const getDateTimeRange = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];
        // Prefer full datetime, then construct from date+time
        const depDateStr = firstLeg.departure_datetime || (firstLeg.departure_date && firstLeg.departure_time ? `${firstLeg.departure_date}T${firstLeg.departure_time}` : null) || firstLeg.departure_date || firstLeg.departure_time;
        const arrDateStr = lastLeg.arrival_datetime || (lastLeg.arrival_date && lastLeg.arrival_time ? `${lastLeg.arrival_date}T${lastLeg.arrival_time}` : null) || lastLeg.arrival_date || lastLeg.arrival_time;

        if (depDateStr && arrDateStr) {
          const depDate = formatDateUtil(depDateStr, prefs);
          const depTime = formatTimePreserveTimezone(depDateStr, prefs) || formatTimeUtil(depDateStr, prefs);
          const arrDate = formatDateUtil(arrDateStr, prefs);
          const arrTime = formatTimePreserveTimezone(arrDateStr, prefs) || formatTimeUtil(arrDateStr, prefs);
          const startStr = depTime ? `${depDate} ${depTime}` : depDate;
          const endStr = arrTime ? `${arrDate} ${arrTime}` : arrDate;
          return { start: startStr, end: endStr, isRange: true };
        } else if (depDateStr) {
          const depDate = formatDateUtil(depDateStr, prefs);
          const depTime = formatTimePreserveTimezone(depDateStr, prefs) || formatTimeUtil(depDateStr, prefs);
          return { start: depTime ? `${depDate} ${depTime}` : depDate, end: null, isRange: false };
        }
      }
    }
    if (item.start_datetime) {
      const date = formatDateUtil(item.start_datetime, prefs);
      const time = formatTimePreserveTimezone(item.start_datetime, prefs) || formatTimeUtil(item.start_datetime, prefs);
      return { start: time ? `${date} ${time}` : date, end: null, isRange: false };
    }
    return null;
  };

  // Get passenger name for flights
  const getPassengerName = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      if (cd.traveler_name) return cd.traveler_name;
      const passengers = cd.passengers || [];
      if (passengers.length > 0) {
        const passengerName = passengers[0]?.name || passengers[0];
        if (typeof passengerName === 'string') return passengerName;
      }
    }
    return null;
  };

  // Get additional details
  const getDetails = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight') {
      const legs = cd.flight_legs || cd.legs || [];
      if (legs.length > 0) {
        return legs.map((leg) => {
          const airline = leg.airline_name || leg.airline || '';
          const flight = leg.flight_number || '';
          return airline && flight ? `${airline} ${flight}` : (flight || airline);
        }).filter(Boolean).join(' • ');
      }
      const airline = cd.airline_name || cd.airline || '';
      const flight = cd.flight_number || '';
      return airline && flight ? `${airline} ${flight}` : (flight || airline);
    }
    if (item.component_type === 'hotel') {
      const checkIn = cd.check_in_date ? formatDateUtil(cd.check_in_date, prefs) : null;
      const checkOut = cd.check_out_date ? formatDateUtil(cd.check_out_date, prefs) : null;
      if (checkIn && checkOut) return `Check-in: ${checkIn} • Check-out: ${checkOut}`;
    }
    if (item.component_type === 'rental_car' || item.component_type === 'car_rental') {
      return cd.car_type || cd.vehicle_type || '';
    }
    return '';
  };


  return (
    <div
      className="rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-lg"
      style={{
        backgroundColor: `${typeStyle.backgroundColor}`,
        borderColor: typeStyle.borderColor
      }}
    >
      {/* Header: Icon + Title + Type Badge */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: typeStyle.borderColor,
            color: typeStyle.iconColor
          }}
        >
          {getTypeIcon(item.component_type)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-semibold text-ui-ink leading-tight">
              {item.title || getTypeName(item.component_type)}
            </h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeStyle.badgeColor}`}>
              {getTypeName(item.component_type)}
            </span>
            {item.is_confidential_inbox && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-red-bg text-ui-red-fg border border-ui-red-line">
                <Lock className="h-3 w-3" />
                Confidential
              </span>
            )}
          </div>

          {(() => {
            const dateTimeRange = getDateTimeRange();
            if (dateTimeRange) {
              return (
                <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    {dateTimeRange.isRange
                      ? `${dateTimeRange.start} - ${dateTimeRange.end}`
                      : dateTimeRange.start}
                  </span>
                </div>
              );
            }
            return null;
          })()}

          {getLocation() && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{getLocation()}</span>
            </div>
          )}

          {getPassengerName() && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mb-1">
              <User className="w-3.5 h-3.5" />
              <span>{getPassengerName()}</span>
            </div>
          )}

          {getDetails() && (
            <div className="text-sm text-ui-muted">
              {getDetails()}
            </div>
          )}

          {item.confirmation_number && (
            <div className="flex items-center gap-1 text-sm text-ui-muted mt-1">
              <Hash className="w-3.5 h-3.5" />
              <span>{item.confirmation_number}</span>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(item.id)}
          disabled={isDeleting}
          className="h-8 w-8 text-ui-faint hover:text-ui-red-fg hover:bg-ui-red-bg flex-shrink-0"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Assignment Section - Two dropdowns */}
      <div className="pt-3 border-t border-ui-divider space-y-2">
        {/* Executive Dropdown */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <select
              value={selectedExecutiveId || ''}
              onChange={(e) => onExecutiveSelect(item.id, e.target.value)}
              className="w-full h-9 pl-3 pr-8 text-sm border border-ui-line rounded-lg bg-ui-surface text-ui-ink-2 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid appearance-none"
            >
              <option value="">Assign to executive...</option>
              {executives.map((exec) => (
                <option key={exec.id} value={exec.id}>
                  {exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim() || 'Unnamed'}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ui-faint pointer-events-none" />
          </div>
        </div>

        {/* Trip Dropdown + File Button */}
        <div className="space-y-2">
          <div className="relative flex-1">
            <select
              value={selectedTripId || ''}
              onChange={(e) => onTripSelect(item.id, e.target.value)}
              className="w-full h-9 pl-3 pr-8 text-sm border border-ui-line rounded-lg bg-ui-surface text-ui-ink-2 focus:outline-none focus:ring-2 focus:ring-ui-violet-solid focus:border-ui-violet-solid appearance-none"
            >
              <option value="">Assign to trip...</option>
              {trips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.title || 'Untitled Trip'}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ui-faint pointer-events-none" />
          </div>
          <InlineAssignment componentId={item.id} tripId={selectedTripId} executiveId={selectedExecutiveId}
            busy={isFiling} onApply={preview => onFile(item.id, preview)} />
        </div>
        <p className="text-xs text-ui-muted">
          Select an executive and/or trip to assign this item.
        </p>
      </div>
    </div>
  );
};

/**
 * UnfiledTimelineView - Displays all unfiled items in Timeline 2.0 format
 * Shows EA inbox items (unassigned) at the top, followed by executive-assigned items
 */
const UnfiledTimelineView = ({ onItemAssigned }) => {
  const [items, setItems] = useState([]);
  const [assignmentNotice, setAssignmentNotice] = useState('');
  const [trips, setTrips] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrips, setSelectedTrips] = useState({}); // { componentId: tripId }
  const [selectedExecutives, setSelectedExecutives] = useState({}); // { componentId: executiveId }
  const [assigningId, setAssigningId] = useState(null);
  const [filingId, setFilingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [revealingId, setRevealingId] = useState(null);
  const [concealingId, setConcealingId] = useState(null);

  // Fetch all unfilled components
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listUnfilledComponents();
      console.log('UnfiledTimelineView: Fetched items', result);
      const components = result.unfilled_components || [];
      console.log('UnfiledTimelineView: Components count:', components.length);
      if (components.length > 0) {
        console.log('UnfiledTimelineView: First item:', components[0]);
        console.log('UnfiledTimelineView: Sample fields - executive_id:', components[0].executive_id, 'is_unassigned:', components[0].is_unassigned);
      }
      setItems(components);
    } catch (err) {
      console.error('Error fetching unfilled components:', err);
      setError('Failed to load unfiled items');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch trips for the dropdown
  const fetchTrips = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/trips?limit=100`, { headers });
      setTrips(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching trips:', err);
    }
  }, []);

  // Fetch executives for the dropdown
  const fetchExecutives = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/executives`, { headers });
      setExecutives(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching executives:', err);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchTrips();
    fetchExecutives();
  }, [fetchItems, fetchTrips, fetchExecutives]);

  // Handle SSE events for unfiled components (real-time updates)
  const handleUnfiledComponentEvent = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  useUnfiledComponentEvents(handleUnfiledComponentEvent, [onItemAssigned]);

  const handleTripSelect = (componentId, tripId) => {
    setSelectedTrips(prev => ({
      ...prev,
      [componentId]: tripId
    }));
  };

  const handleExecutiveSelect = (componentId, executiveId) => {
    setSelectedExecutives(prev => ({
      ...prev,
      [componentId]: executiveId
    }));
  };

  // Handle filing an EA inbox item (with executive and/or trip)
  const handleFile = async (componentId, acceptedPreview) => {
    const executiveId = selectedExecutives[componentId];
    const tripId = selectedTrips[componentId];

    if (!executiveId && !tripId) return;

    setFilingId(componentId);
    try {
      const result = await fileUnfilledComponent(componentId, { executiveId, tripId, acceptedPreview });

      setAssignmentNotice(result.warnings?.join(' ') || (result.updates?.some(r => r.action === 'update') ? 'Booking updated. Previous itineraries and source documents are available in the booking.' : 'Item assigned.'));
      if (result.filed_to_trip) {
        // Item was filed to a trip - remove from list
        setItems(prev => prev.filter(item => item.id !== componentId));
        if (onItemAssigned) {
          onItemAssigned(componentId, tripId);
        }
      } else {
        // Item was assigned to executive only - update the item in place
        setItems(prev => prev.map(item =>
          item.id === componentId
            ? { ...item, executive_id: executiveId, executive_name: result.executive_name, is_unassigned: false }
            : item
        ));
      }

      // Clear selections
      setSelectedTrips(prev => {
        const next = { ...prev };
        delete next[componentId];
        return next;
      });
      setSelectedExecutives(prev => {
        const next = { ...prev };
        delete next[componentId];
        return next;
      });
    } catch (err) {
      console.error('Error filing component:', err);
      if (err.message !== 'Assignment cancelled') alert(err.response?.data?.detail || err.message || 'Failed to file item');
    } finally {
      setFilingId(null);
    }
  };

  // Handle assigning an executive-assigned item to a trip
  const handleAssign = async (componentId, acceptedPreview) => {
    const tripId = selectedTrips[componentId];
    if (!tripId) return;

    setAssigningId(componentId);
    try {
      const result = await assignUnfilledComponent(componentId, tripId, acceptedPreview);
      setAssignmentNotice(result.warnings?.join(' ') || 'Booking assigned. Previous itineraries and source documents remain available.');
      setItems(prev => prev.filter(item => item.id !== componentId));
      setSelectedTrips(prev => {
        const next = { ...prev };
        delete next[componentId];
        return next;
      });
      if (onItemAssigned) {
        onItemAssigned(componentId, tripId);
      }
    } catch (err) {
      console.error('Error assigning component:', err);
      if (err.message !== 'Assignment cancelled') alert(err.response?.data?.detail || err.message || 'Failed to assign item to trip');
    } finally {
      setAssigningId(null);
    }
  };

  const handleDelete = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this unfiled item?')) {
      return;
    }

    setDeletingId(componentId);
    try {
      await deleteUnfilledComponent(componentId);
      setItems(prev => prev.filter(item => item.id !== componentId));
    } catch (err) {
      console.error('Error deleting component:', err);
      alert('Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle revealing a confidential placeholder
  const handleReveal = async (componentId) => {
    setRevealingId(componentId);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${apiBase}/unfilled-components/${componentId}/reveal`,
        {},
        { headers }
      );
      // Update the item in state with the full component data
      if (response.data.component) {
        setItems(prev => prev.map(item =>
          item.id === componentId ? response.data.component : item
        ));
      } else {
        // Fallback: refetch all items
        fetchItems();
      }
    } catch (err) {
      console.error('Error revealing component:', err);
      alert('Failed to reveal confidential item');
    } finally {
      setRevealingId(null);
    }
  };

  // Handle concealing a revealed confidential component
  const handleConceal = async (componentId) => {
    setConcealingId(componentId);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(
        `${apiBase}/unfilled-components/${componentId}/conceal`,
        {},
        { headers }
      );
      // Refetch items to get the placeholder
      fetchItems();
    } catch (err) {
      console.error('Error concealing component:', err);
      alert('Failed to hide confidential item');
    } finally {
      setConcealingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-ui-muted">
        <Loader2 className="h-6 w-6 animate-spin mr-3" />
        Loading unfiled items...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ui-red-fg mb-2">{error}</div>
        <Button variant="outline" onClick={fetchItems}>
          Try Again
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-ui-muted">
        <Inbox className="h-16 w-16 mb-4 text-ui-faint" />
        <h3 className="text-lg font-semibold text-ui-ink-3 mb-2">No Unfiled Items</h3>
        {assignmentNotice && <p role="status">{assignmentNotice}</p>}
        <p className="text-sm text-ui-muted text-center max-w-md">
          Items received via dedicated email addresses will appear here until they are assigned to a trip.
        </p>
      </div>
    );
  }

  // Separate items into EA inbox (unassigned) and executive-assigned
  const eaInboxItems = items.filter(item => item.is_unassigned || !item.executive_id);
  const executiveItems = items.filter(item => !item.is_unassigned && item.executive_id);

  // Debug logging
  console.log('UnfiledTimelineView: Total items:', items.length, 'EA Inbox:', eaInboxItems.length, 'Executive:', executiveItems.length);

  // Group executive items by executive name
  const itemsByExecutive = executiveItems.reduce((acc, item) => {
    const execName = item.executive_name || 'Unknown Executive';
    if (!acc[execName]) {
      acc[execName] = [];
    }
    acc[execName].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {assignmentNotice && <p role="status">{assignmentNotice}</p>}
      {/* EA Inbox Section - shown first if there are unassigned items */}
      {eaInboxItems.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-ui-amber-bg rounded-full flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-ui-amber-fg" />
            </div>
            <h3 className="text-lg font-semibold text-ui-ink">EA Inbox</h3>
            <span className="text-sm text-ui-muted">
              ({eaInboxItems.length} item{eaInboxItems.length !== 1 ? 's' : ''})
            </span>
          </div>
          <p className="text-sm text-ui-muted mb-4">
            Items received via EA email that need to be assigned to an executive and/or trip
          </p>

          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {eaInboxItems.map((item) => (
              item.is_placeholder ? (
                <ConfidentialPlaceholder
                  key={item.id}
                  component={item}
                  onReveal={handleReveal}
                  onConceal={handleConceal}
                  isRevealing={revealingId === item.id}
                  isConcealing={concealingId === item.id}
                  isRevealed={false}
                  showConcealOption={false}
                />
              ) : (
                <EAUnfiledItemCard
                  key={item.id}
                  item={item}
                  trips={trips}
                  executives={executives}
                  selectedExecutiveId={selectedExecutives[item.id]}
                  selectedTripId={selectedTrips[item.id]}
                  onExecutiveSelect={handleExecutiveSelect}
                  onTripSelect={handleTripSelect}
                  onFile={handleFile}
                  onDelete={handleDelete}
                  isFiling={filingId === item.id}
                  isDeleting={deletingId === item.id}
                />
              )
            ))}
          </div>
        </div>
      )}

      {/* Divider if both sections have items */}
      {eaInboxItems.length > 0 && Object.keys(itemsByExecutive).length > 0 && (
        <hr className="border-ui-line" />
      )}

      {/* Executive Sections - items already assigned to executives */}
      {Object.entries(itemsByExecutive).map(([execName, execItems]) => (
        <div key={execName}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-ui-violet-bg rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-ui-violet-fg" />
            </div>
            <h3 className="text-lg font-semibold text-ui-ink">{execName}</h3>
            <span className="text-sm text-ui-muted">
              ({execItems.length} item{execItems.length !== 1 ? 's' : ''})
            </span>
          </div>

          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {execItems.map((item) => (
              item.is_placeholder ? (
                <ConfidentialPlaceholder
                  key={item.id}
                  component={item}
                  onReveal={handleReveal}
                  onConceal={handleConceal}
                  isRevealing={revealingId === item.id}
                  isConcealing={concealingId === item.id}
                  isRevealed={false}
                  showConcealOption={false}
                />
              ) : (
                <UnfiledItemCard
                  key={item.id}
                  item={item}
                  trips={trips}
                  selectedTripId={selectedTrips[item.id]}
                  onTripSelect={handleTripSelect}
                  onAssign={handleAssign}
                  onDelete={handleDelete}
                  isAssigning={assigningId === item.id}
                  isDeleting={deletingId === item.id}
                />
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UnfiledTimelineView;
